import type { Column, InputTableQuerySpec } from "@/graphql.ts";
import type { ITableColumn } from "@/redux/modules/table-data.ts";
import {
  ATTRIBUTE_TYPE,
  BLAST_RESULT_FIELDS,
  CELL_TYPE,
  COLUMN_DYNAMIC_TYPES,
  COS_PARENT_ID,
  ENTITY_TYPE,
} from "@/settings/settings.ts";
import CoordinateListNameCell from "@/ui-modules/core/grid/cells/CoordinateListNameCell.tsx";
import LockedCell from "@/ui-modules/core/grid/cells/core/LockedCell.tsx";
import JobStatusCell from "@/ui-modules/core/grid/cells/JobStatusCell.tsx";
import JobTitleCell from "@/ui-modules/core/grid/cells/JobTitleCell.tsx";
import ParentStatusCell from "@/ui-modules/core/grid/cells/ParentStatusCell.tsx";
import SampleStatusCell from "@/ui-modules/core/grid/cells/SampleStatusCell.tsx";

import type { ICellComponent } from "./cell-base-interfaces.js";
import AlternativeVariantProfileCell from "./cells/AlternativeVariantProfileCell.js";
import AttributeDefaultCell from "./cells/AttributeDefaultCell.js";
import AttributeNameAdminCell from "./cells/AttributeNameAdminCell.js";
import BlastGenomeCell from "./cells/BlastGenomeCell.js";
import BlastQueryCell from "./cells/BlastQueryCell.js";
import BlastScoreCell from "./cells/BlastScoreCell.js";
import BlastSequenceCell from "./cells/BlastSequenceCell.js";
import CategoriesAdminCell from "./cells/CategoriesAdminCell.js";
import CategoryImageCell from "./cells/CategoryImageCell.js";
import CellDataChecker from "./cells/CellDataChecker.js";
import ActionCell from "./cells/core/ActionCell.js";
import { OptimisticBooleanCell } from "./cells/core/BooleanCell.js";
import CheckboxCell from "./cells/core/CheckboxCell.js";
import DateCell from "./cells/core/DateCell.js";
import FileCell from "./cells/core/FileCell.js";
import HyperlinkCell from "./cells/core/HyperlinkCell.js";
import { LinkCell } from "./cells/core/LinkCell.js";
import LinkListCell, { correspondsToFieldPath } from "./cells/core/LinkListCell.js";
import TableCellNumber from "./cells/core/TableCellNumber.js";
import TableCellOrganism from "./cells/core/TableCellOrganism.js";
import TableCellText from "./cells/core/TableCellText.js";
import DynamicTermCell from "./cells/DynamicTermCell.js";
import EditDataValidation from "./cells/EditDataValidation.js";
import { BooleanCellEditor } from "./cells/editors/BooleanCellEditor.js";
import { ColorCell } from "./cells/editors/EditableColorCell.js";
import { EditableFileCell } from "./cells/editors/FileCellEditor.js";
import EventResourceCell from "./cells/EventResourceCell.js";
import { FileNameCell } from "./cells/FileNameCell.js";
import FileSizeCell from "./cells/FileSizeCell.js";
import GeneTypeCell from "./cells/GeneTypeCell.js";
import GeneTypesListCell from "./cells/GeneTypesListCell.js";
import GenomeCell from "./cells/GenomeCell.js";
import HitAccessionCell from "./cells/HitAccessionCell.js";
import HitListCell from "./cells/HitListCell.js";
import HitRankCell from "./cells/HitRankCell.js";
import HspCountCell from "./cells/HspCountCell.js";
import HspRankCell from "./cells/HspRankCell.js";
import { JavascriptScriptingCell } from "./cells/JavascriptScriptingCell.js";
import LinkifiedCell from "./cells/LinkifiedCell.js";
import MemberRoleCell from "./cells/MemberRoleCell.js";
import { MissingDataCell } from "./cells/MissingDataCell.js";
import ParentCell from "./cells/ParentCell.js";
import SampleParentsCell from "./cells/ParentsCell.js";
import PasswordStatusCell from "./cells/PasswordStatusCell.js";
import PhenotypeCell from "./cells/PhenotypeCell.js";
import PlaybookAdminCell from "./cells/PlaybookAdminCell.js";
import PlaybookEventStatusCell from "./cells/PlaybookEventStatusCell.js";
import PlaybookEventTimestampCell from "./cells/PlaybookEventTimestampCell.js";
import PlaybookStatusCell from "./cells/PlaybookStatusCell.js";
import ProjectCell from "./cells/ProjectCell.js";
import { ProjectMemberActionCell } from "./cells/ProjectMemberActionCell.js";
import SampleCell from "./cells/SampleCell.js";
import { SequenceLinkCell } from "./cells/SequenceLinkCell.js";
import SequenceTypeCell from "./cells/SequenceTypeCell.js";
import SpeciesCell from "./cells/SpeciesCell.js";
import { SqlScriptingCell } from "./cells/SqlScriptingCell.js";
import StrandCell from "./cells/StrandCell.js";
import StrandListCell from "./cells/StrandListCell.js";
import SystemSessionNameCell from "./cells/SystemSessionNameCell.js";
import ToolAdminCell from "./cells/ToolAdminCell.js";
import TranscriptsCell, { TRANSCRIPTS_SEQUENCEID_PATTERN } from "./cells/TranscriptsCell.js";
import UsernameAdminCell from "./cells/UsernameAdminCell.js";
import UserStatusCell from "./cells/UserStatusCell.js";
import VariantAlleleCell from "./cells/VariantAlleleCell.js";
import VariantEffectCell from "./cells/VariantEffectCell.js";
import VariantEffectsListCell from "./cells/VariantEffectsListCell.js";
import VariantProfileCell from "./cells/VariantProfileCell.js";
import VariantTypeCell from "./cells/VariantTypeCell.js";

// first group in regexp, defines the extractor : RegExp for cell renderer must contain at least one group for capturing of the data extraction path
export const EXTERNAL_REFERENCE_PATTERN = "((.*.)?externalReference\\d*).name$";

export const checkboxDummyColumn: Partial<Column> = {
  cellType: CELL_TYPE.CHECKBOX,
};

export const lockedDummyColumn: Partial<Column> = {
  cellType: CELL_TYPE.LOCKED,
};

// compare function generator for cellType comparison
export const cellTypeToCellRenderer = (cellType: string, component: any) => (colProps: Partial<Column>) =>
  colProps.cellType === cellType && component;

// compare function generator for field comparison
const cellFieldToCellRenderer = (field: string, component: any) => (colProps: Partial<Column>) =>
  correspondsToFieldPath(colProps.field, field) && component;

// compare function generator for columnSet/representedColumn comparison e.g. parent name column
const cellAttributeToCellRenderer =
  (columnSetId: number, representedColumnName: string, component: any) => (colProps: Partial<Column>) =>
    colProps.attribute?.columnSet?.id === columnSetId &&
    colProps.attribute?.representedColumn === representedColumnName &&
    component;

const cellAttributeTypeToCellRenderer =
  (attributeType: ATTRIBUTE_TYPE, component: any) => (colProps: Partial<Column>) =>
    colProps.attribute?.attributeType?.id === attributeType && component;

const cellFieldPatternToCellRenderer = (fieldPattern: string, component: any) => (colProps: Partial<Column>) =>
  new RegExp(fieldPattern).test(colProps.field) && component;

// compare function generator for field comparison + specific for entity type
const cellFieldAndEntityTypeToCellRenderer =
  (field: string, entityType: string, component: any) =>
  (colProps: Partial<Column>, tableQuerySpec: InputTableQuerySpec = {}) =>
    colProps.field == field && tableQuerySpec.resultType === entityType && component;

const dynamicFieldToCellRenderer = (dynamicField: string, component: any) => (colProps: Partial<Column>) =>
  colProps.dynamicType === dynamicField && component;

export type ICellRendererMapping = (
  colProps: Partial<Column>,
  tableQuerySpec: Partial<InputTableQuerySpec>,
) => ICellComponent;

/**
 * Define the list of matcher functions for cell renderers
 */
const cellRenderersList: ICellRendererMapping[] = [
  /* BASED ON FIRST MATCH */

  dynamicFieldToCellRenderer(COLUMN_DYNAMIC_TYPES.ORGANISM_PROPERTY, PhenotypeCell),

  // transcript name/sequenceId
  cellFieldPatternToCellRenderer(TRANSCRIPTS_SEQUENCEID_PATTERN, TranscriptsCell),
  cellFieldToCellRenderer("transcripts.name", LinkListCell),

  // protein name/sequenceId
  cellFieldToCellRenderer("proteins.sequenceId", LinkListCell),
  cellFieldToCellRenderer("proteinMutations.proteinLocation.protein.sequenceId", LinkListCell),
  cellFieldToCellRenderer("bestBlastResults.protein.sequenceId", LinkListCell),

  // genes.name & geneMutations.gene.name & lowerGenes.name & higherGenes.name
  cellFieldToCellRenderer("genes.name", LinkListCell),
  cellFieldToCellRenderer("relatedGenes.name", LinkListCell),
  cellFieldToCellRenderer("geneMutations.gene.name", LinkListCell),
  cellFieldToCellRenderer("lowerGenes.name", LinkListCell),
  cellFieldToCellRenderer("higherGenes.name", LinkListCell),

  // lowerVariants.name & higherVariants.name
  cellFieldToCellRenderer("lowerVariants.name", LinkListCell),
  cellFieldToCellRenderer("higherVariants.name", LinkListCell),

  // sample parents
  cellFieldToCellRenderer("samples.name", SampleCell),
  cellFieldToCellRenderer("sample.name", SampleCell), // sample-genome association (one-to-one)
  cellFieldToCellRenderer("parents.name", SampleParentsCell),

  // internal & external references
  cellFieldToCellRenderer("externalReferences.displayName", LinkListCell),
  cellFieldToCellRenderer("literatureReferences.displayName", LinkListCell),
  cellFieldToCellRenderer("internalReferences.identifier", LinkListCell),
  cellFieldPatternToCellRenderer(EXTERNAL_REFERENCE_PATTERN, LinkListCell), // dynamic field e.g. externalReference122.name

  // mutation effect
  cellFieldToCellRenderer("proteinMutations.effect", VariantEffectsListCell),
  cellFieldToCellRenderer("geneMutations.effect", VariantEffectsListCell),
  cellFieldToCellRenderer("mutVariants.effect", VariantEffectsListCell),
  cellFieldToCellRenderer("effect", VariantEffectCell),

  // gene/transcript type
  cellFieldToCellRenderer("genes.geneType", GeneTypesListCell),
  cellFieldToCellRenderer("geneType", GeneTypeCell),
  cellFieldToCellRenderer("sequenceType", GeneTypeCell),

  // species (single)
  cellFieldToCellRenderer("species.name", SpeciesCell),

  // contigs
  cellFieldToCellRenderer("regions.contig.sequenceId", LinkListCell),
  cellFieldToCellRenderer("proteinLocations.region.contig.sequenceId", LinkListCell),
  cellFieldToCellRenderer("transcriptLocations.region.contig.sequenceId", LinkListCell),
  cellFieldToCellRenderer("mutVariants.region.contig.sequenceId", LinkListCell),

  // strand
  cellFieldToCellRenderer("proteinLocations.region.strand", StrandListCell),
  cellFieldToCellRenderer("transcriptLocations.region.strand", StrandListCell),
  cellFieldToCellRenderer("relatedGenes.strand", StrandListCell),
  cellFieldToCellRenderer("mutVariants.region.strand", StrandListCell),
  cellFieldToCellRenderer("refRegion.strand", StrandCell),
  cellFieldToCellRenderer("region.strand", StrandCell),

  // variant allele
  cellFieldToCellRenderer("allele", VariantAlleleCell),
  cellFieldToCellRenderer("refAllele", VariantAlleleCell),
  cellFieldToCellRenderer("altAllele", VariantAlleleCell),
  cellFieldToCellRenderer("qiGenotype", VariantAlleleCell),

  // variant profile
  cellFieldToCellRenderer("profile.symbol", VariantProfileCell),
  cellFieldToCellRenderer("profileVariant.symbol", AlternativeVariantProfileCell),

  // variant type
  cellFieldToCellRenderer("variantType", VariantTypeCell),

  // genome name
  cellFieldToCellRenderer("bestBlastResults.protein.genome.name", LinkListCell),
  cellFieldToCellRenderer("mutVariants.genome.name", LinkListCell),
  cellFieldToCellRenderer("organisms.name", LinkListCell),
  cellFieldToCellRenderer("target.organism.name", BlastGenomeCell), // more specific first!
  cellFieldToCellRenderer("target.virtualGenomes.name", LinkListCell),
  cellFieldToCellRenderer("organism.name", GenomeCell), // sample-genome association (one-to-one)
  cellFieldToCellRenderer("genome.name", GenomeCell),
  cellFieldToCellRenderer("refGenome.name", GenomeCell),
  cellFieldToCellRenderer("components.name", LinkListCell),

  cellFieldToCellRenderer("vcfVariantInstances.varGenome.name", LinkListCell),

  // project
  cellFieldToCellRenderer("sample.project.name", ProjectCell),
  cellFieldToCellRenderer("dataset.project.name", ProjectCell),
  cellFieldToCellRenderer("project.name", ProjectCell),

  // protein mutations
  cellFieldToCellRenderer("proteinMutations.name", LinkListCell),

  // coordinate lists
  cellFieldAndEntityTypeToCellRenderer("name", ENTITY_TYPE.COORD_LIST, CoordinateListNameCell),

  cellFieldToCellRenderer("bestBlastResults.alignment", HitListCell),
  cellFieldToCellRenderer("pfamMotifs.name", LinkListCell),
  cellFieldToCellRenderer("isPresent", OptimisticBooleanCell),
  cellFieldToCellRenderer("blastHit.rank", HitRankCell),
  cellFieldAndEntityTypeToCellRenderer("rank", ENTITY_TYPE.BLAST_HIT, HitRankCell),
  cellFieldAndEntityTypeToCellRenderer("rank", ENTITY_TYPE.BLAST_HSP, HspRankCell),
  cellFieldToCellRenderer("hspCount", HspCountCell),
  cellFieldToCellRenderer("maxScore", BlastScoreCell),
  cellFieldToCellRenderer("totalScore", BlastScoreCell),
  cellFieldToCellRenderer("target.sequenceId", HitAccessionCell),
  cellFieldAndEntityTypeToCellRenderer("sequenceId", ENTITY_TYPE.BLAST_HSP, HitAccessionCell),
  cellFieldToCellRenderer("target.type", SequenceTypeCell),
  cellFieldToCellRenderer("target.sequence", BlastSequenceCell),
  cellFieldToCellRenderer("targetSequence", BlastSequenceCell),
  cellFieldToCellRenderer("bestHsp.relatedGenes.sequence", BlastSequenceCell), // BLAST_HITS_TABLE_COLUMNS.
  cellFieldToCellRenderer("email", LinkifiedCell),
  cellFieldToCellRenderer("reports.fileName", FileCell),
  cellFieldToCellRenderer("files.fileName", FileCell),
  cellFieldToCellRenderer("user.username", UsernameAdminCell),
  cellFieldToCellRenderer("user.accountStatus", PasswordStatusCell),
  cellFieldToCellRenderer("attributeName", AttributeNameAdminCell),
  cellFieldToCellRenderer("roleId", MemberRoleCell),
  cellFieldToCellRenderer("projectMemberActions", ProjectMemberActionCell),
  cellFieldToCellRenderer("editAction", ActionCell),
  cellFieldToCellRenderer("attributeDefault", AttributeDefaultCell),
  // cellFieldAndEntityTypeToCellRenderer("playbook.name", ENTITY_TYPE.EVENT, PlaybookCell), // for playbook events listing
  cellFieldToCellRenderer("playbook.name", PlaybookAdminCell), // playbook admin editing
  cellFieldToCellRenderer("playbook.enabled", PlaybookStatusCell), // playbook admin
  cellFieldToCellRenderer("playbookCapability.enabled", PlaybookStatusCell), // user role admin
  cellFieldToCellRenderer("category.name", CategoriesAdminCell), // playbook admin editing
  cellFieldToCellRenderer("category.imageName", CategoryImageCell), // playbook admin editing
  cellFieldToCellRenderer("user.isActive", UserStatusCell),
  cellFieldToCellRenderer("results.file.fileName", EventResourceCell),
  cellFieldToCellRenderer("file.size", FileSizeCell),
  cellFieldToCellRenderer("file.sequences.identifier", SequenceLinkCell),

  cellFieldToCellRenderer(BLAST_RESULT_FIELDS.QUERY_ACCESSION, BlastQueryCell),
  cellFieldToCellRenderer(BLAST_RESULT_FIELDS.HIT_MAX_SCORE, BlastScoreCell),

  cellAttributeToCellRenderer(COS_PARENT_ID, "name", ParentCell), // e.g. parent name column
  cellAttributeToCellRenderer(COS_PARENT_ID, "status", ParentStatusCell), // e.g. parent status column
  cellAttributeTypeToCellRenderer(ATTRIBUTE_TYPE.HYPERLINK, HyperlinkCell),
  cellAttributeTypeToCellRenderer(ATTRIBUTE_TYPE.DYNAMIC_TERMS, DynamicTermCell),

  cellFieldToCellRenderer("sqlStatement", SqlScriptingCell),
  cellFieldToCellRenderer("formulaText", JavascriptScriptingCell),
  cellFieldToCellRenderer("coloringRule", JavascriptScriptingCell),
  cellFieldToCellRenderer("color", ColorCell),
  cellFieldToCellRenderer("tool.name", ToolAdminCell),

  cellFieldAndEntityTypeToCellRenderer("name", ENTITY_TYPE.ANALYST_SESSION, SystemSessionNameCell),
  cellFieldAndEntityTypeToCellRenderer("created", ENTITY_TYPE.EVENT, PlaybookEventTimestampCell),
  cellFieldAndEntityTypeToCellRenderer("status", ENTITY_TYPE.SAMPLE, SampleStatusCell),
  cellFieldAndEntityTypeToCellRenderer("status", ENTITY_TYPE.EVENT, PlaybookEventStatusCell),

  /* admin jobs */
  cellFieldToCellRenderer("job.status", JobStatusCell),
  cellFieldToCellRenderer("job.title", JobTitleCell),
  // cellFieldAndEntityTypeToCellRenderer("name", ENTITY_TYPE.WORKFLOW_SESSION, SystemSessionNameCell),

  /*************************/

  // SINGLE entity name & sequenceId
  cellFieldToCellRenderer("name", LinkCell),
  cellFieldToCellRenderer("identifier", LinkCell),
  cellFieldToCellRenderer("sequenceId", LinkCell),

  /*************************/

  // general cell renderers for each cell type
  cellTypeToCellRenderer(CELL_TYPE.STRING, TableCellText),
  cellTypeToCellRenderer(CELL_TYPE.TEXT, TableCellText),
  cellTypeToCellRenderer(CELL_TYPE.NUMBER, TableCellNumber),
  cellTypeToCellRenderer(CELL_TYPE.INTEGER, TableCellNumber),
  cellTypeToCellRenderer(CELL_TYPE.SCIENTIFIC, TableCellNumber),
  cellTypeToCellRenderer(CELL_TYPE.BOOLEAN, OptimisticBooleanCell),
  cellTypeToCellRenderer(CELL_TYPE.STRING_LIST, TableCellText),
  cellTypeToCellRenderer(CELL_TYPE.NESTED_STRING_LIST, TableCellText),
  cellTypeToCellRenderer(CELL_TYPE.NUMBER_LIST, TableCellText),
  cellTypeToCellRenderer(CELL_TYPE.DATE, DateCell),
  cellTypeToCellRenderer(CELL_TYPE.DATE_TIME, DateCell),
  cellTypeToCellRenderer(CELL_TYPE.CHECKBOX, CheckboxCell),
  cellTypeToCellRenderer(CELL_TYPE.FILE, FileCell),
  cellTypeToCellRenderer(CELL_TYPE.FILENAME, FileNameCell),
  cellTypeToCellRenderer(CELL_TYPE.LOCKED, LockedCell),
];

const editableCellRenderersList: ICellRendererMapping[] = [
  dynamicFieldToCellRenderer(COLUMN_DYNAMIC_TYPES.ORGANISM_PROPERTY, PhenotypeCell),
  cellFieldToCellRenderer("color", ColorCell),
  cellTypeToCellRenderer(CELL_TYPE.STRING, TableCellText),
  cellTypeToCellRenderer(CELL_TYPE.TEXT, TableCellText),
  cellTypeToCellRenderer(CELL_TYPE.NUMBER, TableCellNumber),
  cellTypeToCellRenderer(CELL_TYPE.INTEGER, TableCellNumber),
  cellTypeToCellRenderer(CELL_TYPE.BOOLEAN, BooleanCellEditor),
  cellTypeToCellRenderer(CELL_TYPE.DATE, DateCell),
  cellTypeToCellRenderer(CELL_TYPE.FILE, EditableFileCell),
  cellTypeToCellRenderer(CELL_TYPE.ORGANISM, TableCellOrganism),
];

/**
 * Returns the registered cell renderer component based on the list of matchers (first found is the match):
 * - by field
 * - by field pattern
 * - by column set/represented column name
 * - by cell type
 *
 * @param column
 * @param tableQuerySpec sometimes the field is not enough, we need also the type of the entity to forward to the correct cell renderer
 * @param editMode
 * @returns ICellComponent or null.
 */
export const getCellRenderer = (
  column: Partial<ITableColumn>,
  tableQuerySpec: Partial<InputTableQuerySpec> = {},
  editMode = false,
): ICellComponent => {
  const cellMappings: ICellRendererMapping[] =
    editMode && column.editable ? editableCellRenderersList : cellRenderersList;

  const match: ICellRendererMapping = cellMappings.find(
    (cellRendererMatcher: ICellRendererMapping) => !!cellRendererMatcher(column, tableQuerySpec),
  );

  // this should never happen
  if (!match) {
    const errorMessage = `Cannot find cell renderer for columnField ${column.field} and cellType ${column.cellType}`;
    if (import.meta.env.DEV) {
      return function MissingDataCellErrorMessage(_props) {
        return <MissingDataCell errorMessage={errorMessage} />;
      };
    }
    throw new Error(errorMessage);
  }

  const cellComponent: ICellComponent = match(column, tableQuerySpec);

  const CellComponent: any = cellComponent;

  const CellWrapperComponent: any = import.meta.env.DEV
    ? (props) => (
        <CellDataChecker {...props}>
          <CellComponent {...props} />
        </CellDataChecker>
      )
    : cellComponent;

  if (editMode && column.editable) {
    return (props) => (
      <EditDataValidation {...props}>
        <CellWrapperComponent {...props} />
      </EditDataValidation>
    );
  } else {
    return CellWrapperComponent;
  }
};
